import { Address } from 'viem'
import { InvestmentNftMetadata } from '@domain/funds/types.ts'
import { StrapiMedia, StrapiRepeatableComponent, StrapiSingleComponent } from './common'
import { StrapiProjectData } from './StrapiProjectData'
import { FundStage } from '@domain/funds'
import { StrapiCommunityFund } from '@types'
import { FundType } from '@domain/funds/types/FundType.ts'
import { FundFilterType } from '@domain/funds/types/FundFilterType'
import type { AktaEnrichmentSnapshot } from '@domain/pre-ipo-access/akta/aktaEnrichment.types'

export type StrapiFundData = {
  fundName: string
  fundDescription: string
  communityApproved: boolean
  createdAt: string
  updatedAt: string
  publishedAt: string
  locale: string
  fundFilterType: FundFilterType
  fundSummary: string
  fundResultDate: string | null
  fundPassedFailed: boolean | null
  fundUiSection: string | null
  trending: boolean | null
  preIpoAccess: boolean | null
  /** True when the underlying company has gone public (Strapi `public_ipo`). */
  publicIpo?: boolean | null
  /** Public equity ticker used after IPO (e.g. `spcx`, `CRBS`). */
  ticker?: string | null
  promoteOnMainPage: boolean | null
  numberOfNftHolders: number | null
  raiseStartDate: string | null
  raiseEndDate: string | null
  deployStartDate: string | null
  deployEndDate: string | null
  fundClosedDate: string | null
  referenceValue: string | null
  betaOnly: boolean
  fundAddress: Address
  localizations: {
    data: string[]
  }
  projects: StrapiRepeatableComponent<StrapiProjectData>
  fundCoverImage: StrapiSingleComponent<StrapiMedia>
  investmentNftLogo: StrapiSingleComponent<StrapiMedia>
  fundCustomDatum: StrapiSingleComponent<{
    evergreenFundData: {
      connectedFunds: StrapiRepeatableComponent<{
        fundName: string
        fundAddress: Address
        projects: StrapiRepeatableComponent<StrapiProjectData>
        fundCoverImage: StrapiSingleComponent<StrapiMedia>
        stage: FundStage
      }>
      nftWallet: Address
    }
  }>
  stage: FundStage
  type: FundType
  TGE_date: string | null
  fundUrlId: string
  airdrop: number | null
  showValuationDetails: boolean
  fundIndex: number | null
  maxInvestmentPercentage: number
  investmentNftMetadata: InvestmentNftMetadata
  minInvestmentValue: number
  communityFundCustomData?: StrapiCommunityFund
  moneyDeployed: number
  moneyReturned: number
  marketplace: StrapiMarketplace
  fundLogo: StrapiSingleComponent<StrapiMedia>
  fundDisplayType: FundDisplayType
  hideOnLandingPage: boolean
  numberOfNftOwners: number | null
  heroSection: {
    heroTagline: string
    promoteOnHomepageHero: boolean
    heroCoverImage: StrapiSingleComponent<StrapiMedia>
  } | null
  /** 2–3 word sector label for invest list cards (e.g. Physical AI). */
  sector?: string | null
  proofLink: string | null
  referralCodes?: string | string[] | null
  cardBullets?: {
    bulletText: string
  }[]
}

export type StrapiPreIpoHomepage = {
  blurb: string
  pageTitle: string
  pageId: string
  pageSubHeading: string
  projectId: string
  pageCover?: StrapiSingleComponent<StrapiMedia> | string | null
  exitStrategy: string
  problem: string
  solution: string
  companyOverview: string
  whyThisDeal: string
  intellectualProperty: string
  businessModel: string
  team: string
  tractionValidation: string
  faqs: {
    id: number
    question: string
    answer: string
  }[]
  investmentHighlights: Record<string, string>
  highlightStats: {
    value: string
    description: string
  }[]
  heroVideoUrl?: string | null
  webCoverImage?: StrapiSingleComponent<StrapiMedia> | null
  carouselImages?: StrapiRepeatableComponent<StrapiMedia> | { data: unknown[] } | null
  opportunityHighlights?: {
    id: number
    opportunityHighlight: string
  }[]
  watchVideo?: {
    id: number
    videoUrl: string
  }[]
  assetTracker?: {
    id?: number
    title?: string
    description?: string
    lastUpdate?: string
    Title?: string
    Description?: string
    Last_Update?: string
  }[]
  /** Optional URL to this company’s 1:1 equity audit / proof document. */
  equityAuditUrl?: string | null
  /** Company sector shown on slice cards (e.g. Aerospace). */
  sector?: string | null
  intelligence?: AktaEnrichmentSnapshot | null
  /** Backed-by / partner logos on the company homepage. */
  images?: StrapiRepeatableComponent<StrapiMedia> | { data: unknown[] } | null
  backers?: {
    id?: number
    name: string
    logo?: StrapiSingleComponent<StrapiMedia> | null
    logoDark?: StrapiSingleComponent<StrapiMedia> | null
  }[]
}

export type FundInvestmentMetadata = {
  name: string
  description: string
  image: string
  external_url: string
  attributes: Array<{ trait_type: string; value: string }>
}

export type StrapiMarketplace = {
  investmentAddress: string
  visibleOnMarketplace: boolean
  collectionDescription: string
  collectionCardDescription: string
  collectionName?: string
}

export enum FundDisplayType {
  Genesis1 = 'genesis1',
  Genesis2 = 'genesis2',
  Capped = 'capped',
  DETF = 'detf',
  CustomCommunity = 'custom_community',
  CustomFundOfFunds = 'custom_fund_of_funds',
}
